import twColors from "tailwindcss/colors";
import { config as baseConfig } from "../config";

const colorCache = new Map();

export function readCssVar(name) {
  if (typeof window === "undefined") return "";
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return v ? v.trim() : "";
}

export function resolveTokenColor(t) {
  if (!t || typeof t !== "string") return t;
  const s = t.trim();
  if (s === "transparent") return "transparent";
  if (s === "black") return twColors.black;
  if (s === "white") return twColors.white;
  if (s.startsWith("#") || s.startsWith("rgb") || s.startsWith("hsl")) return s;
  if (s.startsWith("var(")) {
    const m = s.match(/var\((--[^)]+)\)/);
    const name = m && m[1];
    const v = name ? readCssVar(name) : "";
    return v || s;
  }
  if (colorCache.has(s)) return colorCache.get(s);
  const themeMatch = s.match(
    /^(primary|neutrals|success|warning|danger|info|draft|leave|invoiced)(?:-([a-z0-9]+))?$/
  );
  if (themeMatch) {
    const varName = themeMatch[2]
      ? `--color-${themeMatch[1]}-${themeMatch[2]}`
      : `--color-${themeMatch[1]}`;
    const v = readCssVar(varName);
    const resolved = v || `var(${varName})`;
    colorCache.set(s, resolved);
    return resolved;
  }
  if (s.startsWith("color-")) {
    const varName = `--${s}`;
    const v = readCssVar(varName);
    const resolved = v || `var(${varName})`;
    colorCache.set(s, resolved);
    return resolved;
  }
  const [name, shade] = s.split("-");
  const p = twColors[name];
  if (p) {
    const v = shade ? p[shade] : p.DEFAULT || p["500"];
    if (v) {
      colorCache.set(s, v);
      return v;
    }
  }
  return s;
}

export function resolveColors(tokens) {
  if (!Array.isArray(tokens)) return [];
  return tokens.map(resolveTokenColor);
}

export function getExtents(series, clampMinZero = false) {
  let min = Infinity,
    max = -Infinity;
  for (const s of series) {
    for (const v of s.data || []) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!isFinite(min) || !isFinite(max)) {
    min = 0;
    max = 1;
  }
  if (clampMinZero) min = 0;
  if (min === max) {
    max = min + 1;
  }
  return { min, max };
}

export function getStackedExtents(src, nCats, clampMinZero = false) {
  let min = Infinity,
    max = -Infinity;
  for (let i = 0; i < nCats; i++) {
    let sum = 0;
    for (const s of src) {
      const v = s.data?.[i];
      if (v != null && isFinite(v)) sum += Number(v);
    }
    if (sum < min) min = sum;
    if (sum > max) max = sum;
  }
  if (!isFinite(min) || !isFinite(max)) {
    min = 0;
    max = 1;
  }
  if (clampMinZero) min = 0;
  if (min === max) max = min + 1;
  return { min, max };
}

function niceStep(raw) {
  if (!(raw > 0) || !isFinite(raw)) return 1;
  const exp = Math.floor(Math.log10(raw));
  const f = raw / Math.pow(10, exp);
  let nf = 1;
  if (f <= 1) nf = 1;
  else if (f <= 2) nf = 2;
  else if (f <= 5) nf = 5;
  else nf = 10;
  return nf * Math.pow(10, exp);
}

export function computeNiceTicks(min, max, interiorCount, forceMinZero) {
  const segs = Math.max(
    1,
    (typeof interiorCount === "number" ? interiorCount : 0) + 1
  );
  let niceMin, niceMax, step;
  if (forceMinZero) {
    step = niceStep(Math.max(0, max) / segs);
    niceMin = 0;
    niceMax = Math.ceil(Math.max(0, max) / step) * step;
  } else {
    const anchorMin = Math.min(min, max);
    const anchorMax = Math.max(min, max);
    const range = Math.max(0, anchorMax - anchorMin);
    step = niceStep(range / segs);
    niceMin = Math.floor(anchorMin / step) * step;
    niceMax = Math.ceil(anchorMax / step) * step;
  }
  const ticks = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) ticks.push(v);
  return { ticks, step, niceMin, niceMax };
}

export function formatYLabel(v, step) {
  const cfg = baseConfig.axis?.y || {};
  let decimals = 0;
  if (cfg.decimals === false) {
    const absStep = Math.abs(step || 1);
    if (cfg.format === "compact") {
      decimals = absStep < 1000 ? 1 : 0;
    } else {
      decimals =
        absStep >= 1 ? 0 : Math.min(6, Math.ceil(-Math.log10(absStep)));
    }
  } else if (typeof cfg.decimals === "number") {
    decimals = cfg.decimals;
  }
  const fmt = cfg.format;
  if (fmt === "compact") {
    try {
      return Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: decimals,
      }).format(v);
    } catch {
      return String(v);
    }
  } else if (fmt === "fixed") {
    return Number(v).toFixed(decimals);
  } else if (fmt === "commas") {
    try {
      return Intl.NumberFormat(undefined, {
        maximumFractionDigits: decimals,
      }).format(v);
    } catch {
      return String(v);
    }
  }
  try {
    return Intl.NumberFormat(undefined, {
      maximumFractionDigits: decimals,
    }).format(v);
  } catch {
    return decimals ? Number(v).toFixed(decimals) : String(Math.round(v));
  }
}

export function formatDataValue(v) {
  const cfg = baseConfig.data || {};
  const pre = cfg.prefix || "";
  const suf = cfg.suffix || "";
  const fmt = cfg.format;
  let out = v;
  if (fmt === "compact") {
    try {
      out = Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(v);
    } catch {
      out = String(v);
    }
  } else if (fmt === "commas") {
    try {
      out = Intl.NumberFormat(undefined).format(v);
    } catch {
      out = String(v);
    }
  } else if (fmt === "raw") {
    out = String(v);
  } else {
    try {
      out = Intl.NumberFormat(undefined).format(v);
    } catch {
      out = String(v);
    }
  }
  return `${pre}${out}${suf}`;
}

export function formatXLabel(v, step) {
  const cfg = baseConfig.axis?.x || {};
  let decimals = 0;
  if (cfg.decimals === false) {
    const absStep = Math.abs(step || 1);
    if (cfg.format === "compact") {
      decimals = absStep < 1000 ? 1 : 0;
    } else {
      decimals =
        absStep >= 1 ? 0 : Math.min(6, Math.ceil(-Math.log10(absStep)));
    }
  } else if (typeof cfg.decimals === "number") {
    decimals = cfg.decimals;
  }
  const fmt = cfg.format;
  if (fmt === "compact") {
    try {
      return Intl.NumberFormat(undefined, {
        notation: "compact",
        maximumFractionDigits: decimals,
      }).format(v);
    } catch {
      return String(v);
    }
  } else if (fmt === "fixed") {
    return Number(v).toFixed(decimals);
  } else if (fmt === "commas") {
    try {
      return Intl.NumberFormat(undefined, {
        maximumFractionDigits: decimals,
      }).format(v);
    } catch {
      return String(v);
    }
  }
  try {
    return Intl.NumberFormat(undefined, {
      maximumFractionDigits: decimals,
    }).format(v);
  } catch {
    return decimals ? Number(v).toFixed(decimals) : String(Math.round(v));
  }
}
